/**
 * BespokeBridge – In-Portal Notifications API
 * Supports bell-icon alerts for Member, Volunteer, and Admin portals.
 *
 * Schema: organizations/{orgId}/notifications/{notifId}
 *   recipient_id  – UID of the target user, or 'ADMIN' for admin-wide alerts
 *   type          – NOTIFICATION_TYPES value
 *   title         – short heading
 *   message       – full description
 *   data          – arbitrary payload (event_id, proposal_id, etc.)
 *   read          – boolean
 *   created_at    – Firestore server timestamp
 */
import {
  collection, doc, addDoc, getDocs, updateDoc,
  query, where, orderBy, onSnapshot, limit, writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../utils/firebase';

const orgCol = (orgId, col) => collection(db, 'organizations', orgId, col);
const orgDoc = (orgId, col, id) => doc(db, 'organizations', orgId, col, id);
const now = () => serverTimestamp();

// ─── Notification types ────────────────────────────────────────────────────────
export const NOTIFICATION_TYPES = {
  WAITLIST_PROMOTED:        'WAITLIST_PROMOTED',
  EVENT_PROPOSAL_NEW:       'EVENT_PROPOSAL_NEW',
  EVENT_PROPOSAL_APPROVED:  'EVENT_PROPOSAL_APPROVED',
  EVENT_PROPOSAL_DECLINED:  'EVENT_PROPOSAL_DECLINED',
  EVENT_CANCELLED:          'EVENT_CANCELLED',
  REGISTRATION_CONFIRMED:   'REGISTRATION_CONFIRMED',
  JOB_LISTING_SUBMITTED:    'JOB_LISTING_SUBMITTED',
  JOB_APPLICATION_RECEIVED: 'JOB_APPLICATION_RECEIVED',
  GROUP_REQUEST_SUBMITTED:       'GROUP_REQUEST_SUBMITTED',
  GROUP_REQUEST_APPROVED:        'GROUP_REQUEST_APPROVED',
  GROUP_REQUEST_DECLINED:        'GROUP_REQUEST_DECLINED',
  BEFRIENDER_REQUEST:            'BEFRIENDER_REQUEST',
  BEFRIENDER_MATCHED_MEMBER:     'BEFRIENDER_MATCHED_MEMBER',
  BEFRIENDER_MATCHED_VOLUNTEER:  'BEFRIENDER_MATCHED_VOLUNTEER',
  VOLUNTEER_ROLE_REQUEST:        'VOLUNTEER_ROLE_REQUEST',
  VOLUNTEER_ROLE_APPROVED:       'VOLUNTEER_ROLE_APPROVED',
  VOLUNTEER_ROLE_REJECTED:       'VOLUNTEER_ROLE_REJECTED',
  EVENT_MATERIAL_SHARED:         'EVENT_MATERIAL_SHARED',
};

// ─── Write ─────────────────────────────────────────────────────────────────────

/**
 * Create a notification.
 * @param {string} orgId
 * @param {string} recipientId  – UID or 'ADMIN' for org-wide admin alert
 * @param {string} type         – NOTIFICATION_TYPES value
 * @param {string} title
 * @param {string} message
 * @param {object} data         – extra payload stored with the notification
 */
export const createNotification = async (orgId, recipientId, type, title, message, data = {}) => {
  const ref = await addDoc(orgCol(orgId, 'notifications'), {
    recipient_id: recipientId,
    type,
    title,
    message,
    data,
    read: false,
    created_at: now(),
  });
  return ref.id;
};

/** Mark a single notification as read */
export const markNotificationRead = async (orgId, notificationId) => {
  await updateDoc(orgDoc(orgId, 'notifications', notificationId), {
    read: true,
    read_at: now(),
  });
};

/** Mark all unread notifications for a user/recipient as read */
export const markAllNotificationsRead = async (orgId, recipientId) => {
  const q = query(
    orgCol(orgId, 'notifications'),
    where('recipient_id', '==', recipientId),
    where('read', '==', false)
  );
  const snap = await getDocs(q);
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.update(d.ref, { read: true, read_at: now() }));
  await batch.commit();
};

// ─── Read ──────────────────────────────────────────────────────────────────────

/** One-shot fetch of recent notifications for a recipient */
export const getNotifications = async (orgId, recipientId, maxCount = 20) => {
  const q = query(
    orgCol(orgId, 'notifications'),
    where('recipient_id', '==', recipientId),
    orderBy('created_at', 'desc'),
    limit(maxCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── Real-time listener ────────────────────────────────────────────────────────

/**
 * Subscribe to unread notifications for a recipient.
 * Returns an unsubscribe function.
 * callback receives an array of notification objects.
 */
export const subscribeToNotifications = (orgId, recipientId, callback) => {
  const q = query(
    orgCol(orgId, 'notifications'),
    where('recipient_id', '==', recipientId),
    where('read', '==', false),
    orderBy('created_at', 'desc'),
    limit(20)
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, () => callback([]));   // silently ignore permission errors
};
